import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import {
  compact,
  flattenDeep,
  intersection,
  isUndefined,
  omitBy,
  some
} from "lodash-es";
import { defer, forkJoin, iif, lastValueFrom, Observable, of } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import {
  HealthRecord,
  HealthRecordAttachment,
  HealthRecordCreate,
  HealthRecordDetailDiagnostic,
  HealthRecordDetailLaboratory,
  HealthRecordDetailSOAP,
  HealthRecordDetailVaccination,
  HealthRecordDiagnostic,
  HealthRecordLight,
  HealthRecordUpdate,
  HealthRecordVaccineCreate,
  ClinicDiaryHealthRecordRow,
  HealthRecordWithAttachmentsAndTicketExtended
} from "../models/health-record";
import { catchError, map, switchMap } from "rxjs/operators";
import { dateUtil } from "src/app/support/date-util";
import {
  AttachmentV2,
  AttachmentV2Create
} from "src/app/backend/models/attachment/attachment";
import { API } from "src/app/backend/api";
import { getHealthRecordFileName } from "src/app/support/health-record";
import { tryEscapeAndParseJSON } from "src/app/support/json";
import { SoapTemplate } from "src/app/backend/models/soap-template";
import { Ticket } from "src/app/backend/models/ticket";
import { WorkOrder } from "src/app/backend/models/work-order/work-order";
import { SoapTemplateBackend } from "src/app/backend/api/soap-template.backend";
import { WorkOrderBackend } from "src/app/backend/api/work-order/work-order.backend";
import { TicketBackend } from "src/app/backend/api/ticket.backend";
import { sntTranslate } from "src/app/support/translate";

@Injectable()
export class HealthRecordBackend {
  private http = inject(HttpClient);
  private readonly soapTemplateBackend = inject(SoapTemplateBackend);
  private readonly workOrderBackend = inject(WorkOrderBackend);
  private readonly ticketBackend = inject(TicketBackend);

  @UserNotify({
    successMessage: marker("Documento creato"),
    errorMessage: marker("Impossibile creare il documento")
  })
  create(
    payload: HealthRecordCreate | HealthRecordVaccineCreate
  ): Observable<HealthRecord> {
    return this.createSilently(payload);
  }

  createSilently(
    payload: HealthRecordCreate | HealthRecordVaccineCreate
  ): Observable<HealthRecord> {
    return this.http.post<HealthRecord>(
      `${environment.snoots.api.healthcare}/health-records`,
      payload
    );
  }

  list(
    request: PageRequest
  ): Observable<
    PaginatedResult<
      HealthRecord<
        | HealthRecordDetailSOAP
        | HealthRecordDetailLaboratory
        | HealthRecordDetailVaccination
      >
    >
  > {
    return this.http
      .get<
        PaginatedResult<
          HealthRecord<
            | HealthRecordDetailSOAP
            | HealthRecordDetailLaboratory
            | HealthRecordDetailVaccination
          >
        >
      >(`${environment.snoots.api.healthcare}/health-records`, {
        params: omitBy(
          {
            p: request.page,
            s: request.pageSize,
            ...request.filterValues
          },
          isUndefined
        )
      })
      .pipe(
        map((result) => ({
          ...result,
          docs: result.docs.map(extendDoc)
        }))
      );
  }

  listAll(
    filters?: Pick<PageRequest, "filterValues">,
    staticFilters?: {
      from?: string | undefined;
      to?: string | undefined;
      ticketId?: string | undefined;
      diagnosisTags?: string[] | undefined;
    }
  ): Observable<
    Array<
      HealthRecord<
        | HealthRecordDetailSOAP
        | HealthRecordDetailLaboratory
        | HealthRecordDetailVaccination
      >
    >
  > {
    return this.list({
      page: 1,
      pageSize: 100000,
      filterValues: filters?.filterValues
    }).pipe(
      map((result) => result.docs),
      map(
        (
          docs: Array<
            HealthRecord<
              | HealthRecordDetailSOAP
              | HealthRecordDetailLaboratory
              | HealthRecordDetailVaccination
            >
          >
        ) => {
          if (!staticFilters) {
            return docs;
          }

          if (staticFilters.ticketId) {
            docs = docs.filter((x) => x.ticket?.id === staticFilters.ticketId);
          }

          if (
            staticFilters.diagnosisTags &&
            staticFilters.diagnosisTags.length > 0
          ) {
            docs = docs.filter((x) => {
              if (x.type === "soap") {
                return (
                  intersection(
                    (<HealthRecord<HealthRecordDetailSOAP>>x).detail?.assessment
                      ?.tags || [],
                    staticFilters.diagnosisTags
                  ).length > 0
                );
              }

              return true;
            });
          }

          if (staticFilters.from) {
            const dateFrom = dateUtil(staticFilters.from);
            docs = docs.filter((x) =>
              dateUtil(x.performedAt).isAfter(dateFrom)
            );
          }

          if (staticFilters.to) {
            const dateTo = dateUtil(staticFilters.to);
            docs = docs.filter((x) => dateUtil(x.performedAt).isBefore(dateTo));
          }

          return docs;
        }
      )
    );
  }

  listAllByPatient(
    id: string,
    filters: {
      from?: string | undefined;
      to?: string | undefined;
      ticketId?: string | undefined;
      diagnosisTags?: string[] | undefined;
      masterCase?: boolean;
      parentId?: string;
      completed?: string;
      type?: string;
      name?: string;
      createdById?: string;
    }
  ): Observable<
    Array<
      HealthRecord<
        | HealthRecordDetailSOAP
        | HealthRecordDetailLaboratory
        | HealthRecordDetailVaccination
      >
    >
  > {
    return this.listAll(
      {
        filterValues: omitBy(
          {
            patientId: id,
            masterCase: filters.masterCase,
            parentId: filters.parentId,
            from: filters.from,
            to: filters.to,
            completed: filters.completed,
            type: filters.type,
            name: filters.name,
            createdById: filters.createdById
          },
          isUndefined
        )
      },
      filters
    );
  }

  listAllRootByPatient(
    id: string,
    filters: {
      from?: string | undefined;
      to?: string | undefined;
      ticketId?: string | undefined;
      diagnosisTags?: string[] | undefined;
    }
  ): Observable<
    Array<
      HealthRecord<
        | HealthRecordDetailSOAP
        | HealthRecordDetailLaboratory
        | HealthRecordDetailVaccination
      >
    >
  > {
    return this.listAllByPatient(id, { ...filters, masterCase: true }).pipe(
      map((result) => result.filter((x) => !x.parentId))
    );
  }

  /**
   * Full SOAP/detail, template, attachments, and ticket — same shape as diary rows.
   * Use when the UI only has a light embed (e.g. segmentation stream).
   */
  loadExtendedWithAttachments(
    id: string
  ): Observable<HealthRecordWithAttachmentsAndTicketExtended> {
    return this.load(id).pipe(
      switchMap((healthRecord) =>
        this.extendHealthRecordWithRelations(healthRecord, {
          includeAttachments: true
        }).pipe(
          map((row) => {
            const attachments: HealthRecordAttachment[] =
              row.attachments ?? ([] as HealthRecordAttachment[]);

            return <HealthRecordWithAttachmentsAndTicketExtended>{
              ...row,
              attachments
            };
          })
        )
      )
    );
  }

  private extendHealthRecordWithRelations(
    healthRecord: HealthRecord<
      | HealthRecordDetailSOAP
      | HealthRecordDetailLaboratory
      | HealthRecordDetailVaccination
      | HealthRecordDetailDiagnostic
    >,
    relations: { includeAttachments: boolean } = { includeAttachments: true }
  ): Observable<ClinicDiaryHealthRecordRow> {
    return forkJoin({
      soapTemplate:
        healthRecord.type === "soap" &&
        (<HealthRecord>healthRecord)?.detail?.templateId
          ? this.soapTemplateBackend.load(
              (<HealthRecord>healthRecord)?.detail?.templateId
            )
          : of(undefined),
      attachments: relations.includeAttachments
        ? this.loadAttachments(healthRecord.id)
        : of(undefined),
      workOrder:
        healthRecord.type === "laboratory" &&
        (<HealthRecordDetailLaboratory>(<unknown>healthRecord.detail))
          ?.workOrderId
          ? this.workOrderBackend.load(
              (<HealthRecordDetailLaboratory>(<unknown>healthRecord.detail))
                .workOrderId!
            )
          : of(undefined),
      ticket: iif(
        () => !!healthRecord.ticket,
        defer(() =>
          this.ticketBackend
            .load({ uuid: healthRecord.ticket!.id })
            .pipe(catchError(() => of(undefined)))
        ),
        of(undefined)
      )
    }).pipe(
      map(
        ({
          soapTemplate,
          attachments,
          ticket,
          workOrder
        }: {
          soapTemplate: SoapTemplate | undefined;
          attachments: HealthRecordAttachment[] | undefined;
          ticket: Ticket | undefined;
          workOrder: WorkOrder | undefined;
        }) => {
          return <ClinicDiaryHealthRecordRow>{
            ...healthRecord,
            attachments,
            ticket,
            soapTemplate,
            workOrder
          };
        }
      )
    );
  }

  listAllExtendedWithAttachmentsByPatient(
    patientId: string,
    filters: {
      from?: string | undefined;
      to?: string | undefined;
      ticketId?: string | undefined;
      diagnosisTags?: string[] | undefined;
      masterCase?: boolean;
      parentId?: string;
      completed?: string;
      type?: string;
      name?: string;
      createdById?: string;
    },
    relations: { includeAttachments: boolean } = { includeAttachments: true }
  ): Observable<Array<ClinicDiaryHealthRecordRow>> {
    return this.listAllByPatient(patientId, filters).pipe(
      switchMap(
        (
          healthRecordList: HealthRecord<
            | HealthRecordDetailSOAP
            | HealthRecordDetailLaboratory
            | HealthRecordDetailVaccination
          >[]
        ) => {
          return iif(
            () => !healthRecordList || healthRecordList.length === 0,
            of([]),
            defer(() =>
              forkJoin(
                healthRecordList.map((healthRecord) =>
                  this.extendHealthRecordWithRelations(healthRecord, relations)
                )
              )
            )
          );
        }
      ),
      map((healthRecordsWithRelations: ClinicDiaryHealthRecordRow[]) => {
        return healthRecordsWithRelations.sort((left, right) => {
          return dateUtil(left.performedAt).isBefore(right.performedAt)
            ? 1
            : -1;
        });
      })
    );
  }

  listAllChildrenByPatient(
    patientId: string,
    parentId: string
  ): Observable<
    Array<
      HealthRecord<
        | HealthRecordDetailSOAP
        | HealthRecordDetailLaboratory
        | HealthRecordDetailVaccination
      >
    >
  > {
    return this.listAllByPatient(patientId, {
      parentId
    });
  }

  listAllChildrenExtendedWithAttachmentsByPatient(
    patientId: string,
    parentId: string,
    relations: { includeAttachments: boolean } = { includeAttachments: true }
  ): Observable<Array<ClinicDiaryHealthRecordRow>> {
    return this.listAllExtendedWithAttachmentsByPatient(
      patientId,
      {
        parentId: parentId
      },
      relations
    ).pipe(map((results) => results.filter((x) => x.parentId === parentId)));
  }

  listAllChildrenDeepExtendedWithAttachmentsByPatient(
    patientId: string,
    parentId: string,
    relations: { includeAttachments: boolean } = { includeAttachments: true }
  ): Observable<Array<ClinicDiaryHealthRecordRow>> {
    return this.listAllExtendedWithAttachmentsByPatient(
      patientId,
      {
        parentId: parentId
      },
      relations
    ).pipe(
      map((results) => results.filter((x) => x.parentId === parentId)),
      switchMap((healthRecords) => {
        return iif(
          () => healthRecords && healthRecords.length > 0,
          defer(() =>
            forkJoin(
              healthRecords.map((x) => {
                return this.listAllChildrenDeepExtendedWithAttachmentsByPatient(
                  patientId,
                  x.id,
                  relations
                );
              })
            )
          ),
          of([])
        ).pipe(
          map((childrenHealthRecords) => {
            return [...healthRecords, ...childrenHealthRecords];
          })
        );
      }),
      map((healthRecordsOfHealhRecords) => {
        return flattenDeep(healthRecordsOfHealhRecords).sort((left, right) => {
          return dateUtil(left.performedAt).isBefore(right.performedAt)
            ? 1
            : -1;
        });
      })
    );
  }

  listAllRootExtendedWithAttachmentsByPatient(
    patientId: string,
    filters: {
      from?: string | undefined;
      to?: string | undefined;
      ticketId?: string | undefined;
      diagnosisTags?: string[] | undefined;
      completed?: string;
      type?: string;
      name?: string;
      createdById?: string;
    },
    relations: { includeAttachments: boolean } = { includeAttachments: true }
  ): Observable<Array<ClinicDiaryHealthRecordRow>> {
    return this.listAllExtendedWithAttachmentsByPatient(
      patientId,
      {
        ...filters,
        masterCase: true
      },
      relations
    ).pipe(map((results) => results.filter((x) => !x.parentId)));
  }

  listAllByWorkOrder(
    id: string,
    filters: {
      from?: string | undefined;
      to?: string | undefined;
      ticketId?: string | undefined;
      diagnosisTags?: string[] | undefined;
    }
  ): Observable<PaginatedResult<HealthRecord<any>>> {
    return this.http
      .get<PaginatedResult<HealthRecord>>(
        `${environment.snoots.api.healthcare}/work-orders/${id}/health-records`
      )
      .pipe(
        map((result) => ({
          ...result,
          docs: result.docs.map(extendDoc)
        }))
      );
  }

  listByPatientAndTicket(
    patientId: string,
    ticketId: string
  ): Observable<
    Array<
      HealthRecord<
        | HealthRecordDetailSOAP
        | HealthRecordDetailLaboratory
        | HealthRecordDetailVaccination
      >
    >
  > {
    return this.listAll(
      {
        filterValues: { patientId }
      },
      {
        ticketId
      }
    );
  }

  listSOAPByPatientAndTicket(
    patientId: string,
    ticketId: string
  ): Observable<Array<HealthRecord<HealthRecordDetailSOAP>>> {
    return this.listByPatientAndTicket(patientId, ticketId).pipe(
      map((healthRecords) =>
        healthRecords
          .filter((x) => x.type === "soap")
          .map((x) => <HealthRecord>x)
      )
    );
  }

  count(filters?: Pick<PageRequest, "filterValues">): Observable<number> {
    return this.listAll(filters).pipe(map((list) => list.length));
  }

  countByPatient(id: string): Observable<number> {
    return this.count({ filterValues: { patientId: id } });
  }

  load(
    id: string
  ): Observable<
    HealthRecord<
      | HealthRecordDetailSOAP
      | HealthRecordDetailLaboratory
      | HealthRecordDetailVaccination
      | HealthRecordDetailDiagnostic
    >
  > {
    return this.http
      .get<
        HealthRecord<
          | HealthRecordDetailSOAP
          | HealthRecordDetailLaboratory
          | HealthRecordDetailVaccination
        >
      >(`${environment.snoots.api.healthcare}/health-records/${id}`)
      .pipe(map(extendDoc));
  }

  loadLight(id: string): Observable<HealthRecordLight> {
    return this.http.get<HealthRecordLight>(
      `${environment.snoots.api.healthcare}/health-records/${id}`,
      {
        params: { light: true }
      }
    );
  }

  updateSilently<T = HealthRecordDetailSOAP>(
    id: string,
    payload: HealthRecordUpdate<T>
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.healthcare}/health-records/${id}`,
      payload
    );
  }

  @UserNotify({
    successMessage: (id: string, payload: HealthRecordUpdate<any>) => {
      switch (payload.type) {
        case "vaccine": {
          return sntTranslate(marker("Vaccino aggiornato"));
        }
        default: {
          return sntTranslate(marker("Documento clinico aggiornato"));
        }
      }
    },
    errorMessage: marker("Impossibile aggiornare il documento")
  })
  update<T = HealthRecordDetailSOAP>(
    id: string,
    payload: HealthRecordUpdate<T>
  ): Observable<void> {
    return this.updateSilently(id, payload);
  }

  @UserNotify({
    successMessage: marker("Documento eliminato"),
    errorMessage: marker("Impossibile eliminare il documento")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/health-records/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Documento completato"),
    errorMessage: marker("Impossibile completare il documento")
  })
  complete(id: string, version: number): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/health-records/${id}/complete`,
      {
        version
      }
    );
  }

  @UserNotify({
    successMessage: marker("Documento riaperto"),
    errorMessage: marker("Impossibile riaprire il documento")
  })
  reopen(id: string, version: number): Observable<void> {
    return this.http.put<void>(
      `${environment.snoots.api.healthcare}/health-records/${id}/reopen`,
      { version }
    );
  }

  addAttachment(
    id: string,
    payload: AttachmentV2Create
  ): Observable<AttachmentV2> {
    return this.http.post<AttachmentV2>(
      API.HEALTHCARE.HEALTH_RECORDS.ID_ATTACHMENTS(id),
      payload
    );
  }

  loadAttachments(id: string): Observable<HealthRecordAttachment[]> {
    return this.http.get<HealthRecordAttachment[]>(
      API.HEALTHCARE.HEALTH_RECORDS.ID_ATTACHMENTS(id)
    );
  }

  deleteAttachment(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.healthcare}/attachments/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Scheda inviata"),
    errorMessage: marker("Impossibile inviare la scheda")
  })
  async send(
    healthRecord: HealthRecord,
    options: {
      email: string;
      storageId: string;
      customerId?: string;
      attachments?: Array<{
        contentType: string;
        fileName: string;
        storageId: string;
      }>;
      note?: string;
    }
  ): Promise<void> {
    await this.sendInternally(
      healthRecord,
      options.email,
      options.customerId,
      options.storageId,
      options.attachments,
      options.note
    );
  }

  @UserNotify({
    successMessage: marker("Scheda inviata"),
    errorMessage: marker("Impossibile inviare la scheda")
  })
  async sendWithoutDocument(
    healthRecord: HealthRecord,
    options: {
      email: string;
      customerId?: string;
      attachments?: Array<{
        contentType: string;
        fileName: string;
        storageId: string;
      }>;
      note?: string;
    }
  ): Promise<void> {
    this.sendInternally(
      healthRecord,
      options.email,
      options.customerId,
      undefined,
      options.attachments,
      options.note
    );
  }

  @UserNotify({
    successMessage: marker("Schede inviate"),
    errorMessage: marker("Impossibile inviare le schede")
  })
  async sendHealthrecords(
    healthRecord: HealthRecord,
    options: {
      email: string;
      storageIds: string[];
      customerId?: string;
      attachments?: Array<{
        contentType: string;
        fileName: string;
        storageId: string;
      }>;
      note?: string;
    }
  ): Promise<void> {
    this.sendWithMutiStorageIdsInternally(
      healthRecord,
      options.email,
      options.storageIds,
      options.customerId,
      options.attachments,
      options.note
    );
  }

  private async sendWithMutiStorageIdsInternally(
    healthRecord: HealthRecord,
    email: string,
    storageIds: string[] | undefined,
    customerId: string | undefined,
    attachments?: Array<{
      contentType: string;
      fileName: string;
      storageId: string;
    }>,
    note?: string
  ): Promise<void> {
    const newAttachments = storageIds?.map((storageId) => ({
      contentType: "application/pdf",
      fileName: getHealthRecordFileName(),
      storageId
    }));

    await lastValueFrom(
      this.http.put(
        `${environment.snoots.api.healthcare}/health-records/${healthRecord.id}/send`,
        omitBy(
          {
            email,
            customerId,
            notes: note,
            attachments: compact([
              ...(newAttachments || []),
              ...(attachments || [])
            ])
          },
          isUndefined
        )
      )
    );
  }

  private async sendInternally(
    healthRecord: HealthRecord,
    email: string,
    customerId: string | undefined,
    storageId: string | undefined,
    attachments?: Array<{
      contentType: string;
      fileName: string;
      storageId: string;
    }>,
    note?: string
  ): Promise<void> {
    await lastValueFrom(
      this.http.put(
        `${environment.snoots.api.healthcare}/health-records/${healthRecord.id}/send`,
        omitBy(
          {
            email,
            customerId,
            notes: note,
            attachments: compact([
              storageId
                ? {
                    contentType: "application/pdf",
                    fileName: getHealthRecordFileName(),
                    storageId
                  }
                : null,
              ...(attachments || [])
            ])
          },
          isUndefined
        )
      )
    );
  }
}

function extendDoc(
  healthRecord: HealthRecord<
    | HealthRecordDetailSOAP
    | HealthRecordDetailLaboratory
    | HealthRecordDetailVaccination
  >
): HealthRecord<
  | HealthRecordDetailSOAP
  | HealthRecordDetailLaboratory
  | HealthRecordDetailVaccination
> {
  switch (healthRecord.type) {
    case "soap": {
      const soapDetail: HealthRecordDetailSOAP | undefined = <
        HealthRecordDetailSOAP
      >healthRecord.detail;

      return {
        ...healthRecord,
        detail: soapDetail
          ? {
              ...healthRecord.detail,
              subjective: soapDetail.subjective
                ? {
                    ...soapDetail.subjective,
                    content:
                      soapDetail.subjective.content &&
                      soapDetail.subjective.content.trim() !== "{}"
                        ? tryEscapeAndParseJSON(soapDetail.subjective.content)
                        : undefined
                  }
                : soapDetail.subjective,
              objective: soapDetail.objective
                ? {
                    ...soapDetail.objective,
                    content:
                      soapDetail.objective.content &&
                      soapDetail.objective.content.trim() !== "{}"
                        ? tryEscapeAndParseJSON(soapDetail.objective.content)
                        : undefined
                  }
                : soapDetail.objective,
              assessment: soapDetail.assessment
                ? {
                    ...soapDetail.assessment,
                    content:
                      soapDetail.assessment.content &&
                      soapDetail.assessment.content.trim() !== "{}"
                        ? tryEscapeAndParseJSON(soapDetail.assessment.content)
                        : undefined
                  }
                : soapDetail.assessment,
              plan: soapDetail.plan
                ? {
                    ...soapDetail.plan,
                    content:
                      soapDetail.plan.content &&
                      soapDetail.plan.content.trim() !== "{}"
                        ? tryEscapeAndParseJSON(soapDetail.plan.content)
                        : undefined
                  }
                : soapDetail.plan
            }
          : healthRecord.detail
      };
    }

    case "laboratory": {
      return extendLaboratory(
        <HealthRecord<HealthRecordDetailLaboratory>>healthRecord
      );
    }

    case "diagnostic": {
      return healthRecord;
    }

    case "vaccine": {
      return healthRecord;
    }

    default: {
      return healthRecord;
    }
  }
}

function extendLaboratory(
  healthRecord: HealthRecord<HealthRecordDetailLaboratory>
): HealthRecord<HealthRecordDetailLaboratory> {
  if (!healthRecord.detail) {
    return healthRecord;
  }

  healthRecord.detail.tests =
    healthRecord.detail.tests?.map((test) => {
      const value = !!test?.value
        ? parseFloat(test?.value?.replace(",", "."))
        : null;

      test.thresholdExceeded =
        !!test.maxRange && !!value ? value! > parseFloat(test.maxRange) : false;

      test.thresholdLower =
        !!test.minRange && !!value ? value! < parseFloat(test.minRange) : false;

      return test;
    }) || [];

  healthRecord.detail.hasSomeParamtersOutOfRange = some(
    healthRecord.detail.tests,
    (x) => !!x.thresholdExceeded || x.thresholdLower
  );

  return healthRecord;
}
